import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { MenuService } from '../../service/menu/menu.service';
import { UserModel } from '@user/model/user.model';
import { UserSessionService } from '@user/service/user-session.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UserComponentPresenter } from '@user/components/user-component.presenter';
import { ApplicationService } from '@shared/application/application.service';
import { merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserModelService } from '@user/model/user.model.service';
import { MenuItem, MenuList } from '../../service/menu/menu.interfaces';
import { UserPermissionService } from '@user/service/user-permission.service';

@Component({
  standalone: false,
  selector: 'app-header-right-content',
  templateUrl: './header-right-content.component.html',
})
export class HeaderRightContentComponent {

  public isLoggedIn: boolean = false;
  public userName: string = '';
  public userEmail: string = '';
  public loginOutAvailable: boolean = false;
  public openCDCProfileAvailable: boolean = false;
  public showUserName: boolean = true;

  public infoMenuItems: MenuList = [];

  @Output() loggedIn = new EventEmitter<void>();

  public settingsEnabled = false;
  public settingsVisible = false;

  constructor(
    private systemConfiguration: SystemConfigurationService,
    private menuService: MenuService,
    private userModel: UserModel,
    private userSessionService: UserSessionService,
    private _userComponentPresenter: UserComponentPresenter,
    private localeResourceManager: LocaleResourceManager,
    private applicationService: ApplicationService,
    private userComponentPresenter: UserComponentPresenter,
    private cdRef: ChangeDetectorRef,
    userModelService: UserModelService,
    private userPermissionService: UserPermissionService,
  ) {
    this.updateLoggedInStatus();
    this.infoMenuItems = this.menuService.getInfoItems();
    merge(userModelService.loginChanged$, userModelService.userProfileChanged$).pipe(takeUntilDestroyed())
      .subscribe(() => this.updateLoggedInStatus());
  }

  public get applicationVersion() {
    return this.applicationService.applicationVersion;
  }

  public get languages() {
    return this.localeResourceManager.getLanguages();
  }

  public get currentLanguage() {
    return this.localeResourceManager.currentLanguage;
  }

  public updateCurrentLanguage(language: string): void {
    this.localeResourceManager.updateCurrentLanguage(language);
  }

  public get user() {
    return this.userModel.user;
  }

  public async login() {
    await this.userComponentPresenter.presentReloginComponent();
    this.cdRef.markForCheck();
    this.loggedIn.emit();
  }

  private updateLoggedInStatus(): void {
    const profile = this.userModel.userProfile;
    this.isLoggedIn = profile && profile.isRegisteredUserAccount();
    if (profile && profile.user) {
      this.loginOutAvailable = true;
      if (profile.user.name && profile.user.surname) {
        this.userName = profile.user.name + ' ' + profile.user.surname;
      } else if (profile.user.name && !profile.user.surname) {
        this.userName = profile.user.name;
      } else if (!profile.user.name && profile.user.surname) {
        this.userName = profile.user.surname;
      } else {
        this.showUserName = false;
      }
    } else {
      this.showUserName = false;
      this.loginOutAvailable = true;
    }
    this.settingsEnabled = this.userPermissionService.isSettingsEnabled();
    this.userEmail = profile && profile.user.login ? profile.user.login : '';
    this.openCDCProfileAvailable = this.userModel.user && this.userModel.user.hasPermission("OPEN_CDC_USER_PROFILE");
    this.updateAvailabilityFields();
    this.infoMenuItems = this.menuService.getInfoItems();
  }

  private updateAvailabilityFields() {
    this.settingsVisible = this.userPermissionService.isNumberMappingsVisible();
    this.settingsEnabled = this.userPermissionService.isSettingsEnabled();
  }

  public runLoggedAction(item: MenuItem) {
    this.menuService.runLoggedAction(item);
  }

  public openCDCProfile(): void {
    window.open(this.systemConfiguration.currentConfiguration.cdcViewUserProfileUrl, '_blank');
  }

  public get isRegisteredUserAccount(): boolean {
    return this.userModel.user?.isRegisteredUserAccount();
  }

  public actionSetting(): void {
    this._userComponentPresenter.presentChoiceActionSettingComponent();
  }

  public manageUserRights(): void {
    this._userComponentPresenter.openUserRightsManagementDialog();
  }

  public async logout(): Promise<void> {
    await this.userSessionService.logout(true);
  }

  public shouldShowLoginButton() {
    return !this.isLoggedIn && !location.pathname.startsWith("/profile");
  }
}
